import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Award } from 'lucide-react';
import { Card, Badge, Button, BloodTypeBadge, ComponentBadge, Alert, Spinner, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const OPTIONAL_COMPONENTS = [
  { value: 'platelets', label: 'Platelets', desc: 'Apheresis appointment, can give every 7 days' },
  { value: 'plasma', label: 'Plasma', desc: 'Apheresis appointment, can give every 28 days' },
];

export default function DonorProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('profile');
  const [componentsDraft, setComponentsDraft] = useState([]);
  const [savingComponents, setSavingComponents] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    Promise.all([
      api.get('/donors/profile').catch(e => {
        if (e.response?.status === 404) setNeedsProfile(true);
        return null;
      }),
      api.get('/donors/screening/questions').catch(() => ({ data: [] })),
    ]).then(([p, q]) => {
      if (p) {
        setProfile(p.data);
        setComponentsDraft(p.data.donatable_components || ['whole_blood']);
      }
      setQuestions(q.data);
      const init = {};
      q.data.forEach(qu => { init[qu.key] = false; });
      setAnswers(init);
    }).finally(() => setLoading(false));
  }, []);

  const componentsChanged = profile
    && JSON.stringify([...componentsDraft].sort()) !== JSON.stringify([...(profile.donatable_components || [])].sort());

  const saveComponents = async () => {
    setSavingComponents(true);
    try {
      const { data } = await api.patch('/donors/profile/components', { donatable_components: componentsDraft });
      setProfile(data);
      setComponentsDraft(data.donatable_components);
    } catch {
      alert('Could not save. Try again.');
    } finally {
      setSavingComponents(false);
    }
  };

  const submitScreening = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/donors/screening', {
        answers: Object.entries(answers).map(([question_key, answer]) => ({ question_key, answer })),
      });
      setEligibility(data);
      if (profile) setProfile(p => ({ ...p, is_eligible: data.is_eligible, eligibility_reason: data.reason }));
    } catch {
      alert('Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
    </PageLayout>
  );

  if (needsProfile) return (
    <PageLayout>
      <Card>
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>🩸</div>
          <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px' }}>
            Finish setting up your donor profile
          </div>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '20px', lineHeight: 1.6, maxWidth: '380px', margin: '0 auto 20px' }}>
            Just a few quick details (blood type, age, weight) — takes under a minute, and you'll need it before you can respond to requests or complete health screening.
          </p>
          <Link to="/donor/setup"><Button variant="primary">Complete profile →</Button></Link>
        </div>
      </Card>
    </PageLayout>
  );

  const infoRows = profile ? [
    ['Blood Type',     profile.blood_type],
    ['Date of Birth',  new Date(profile.date_of_birth).toLocaleDateString()],
    ['Weight',         `${profile.weight_kg} kg`],
    ['Gender',         profile.gender],
    ['Last Donated',   profile.last_donated_at ? new Date(profile.last_donated_at).toLocaleDateString() : 'Never'],
    ['Total Donations',profile.total_donations],
  ] : [];

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)', marginBottom: '16px' }}>
        Donor Profile
      </h1>

      {/* Profile header */}
      {profile && (
        <div style={{
          background: 'linear-gradient(135deg, var(--red-deeper) 0%, var(--red) 100%)',
          borderRadius: 'var(--radius-lg)', padding: isMobile ? '16px' : '22px',
          display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '18px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', right: '-30px', top: '-30px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(4px)',
            border: '2px solid rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '22px', fontWeight: 800, flexShrink: 0,
          }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, zIndex: 1 }}>
            <div style={{ fontSize: '17px', fontWeight: 700, color: 'white', marginBottom: '7px' }}>{user?.name}</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <BloodTypeBadge type={profile.blood_type} />
              <Badge color={profile.is_eligible ? 'green' : 'red'}>
                {profile.is_eligible ? '✓ Eligible' : '✗ Not eligible'}
              </Badge>
              <Badge color="gray">{profile.total_donations} donation{profile.total_donations !== 1 ? 's' : ''}</Badge>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--gray-200)', marginBottom: '16px', gap: '4px' }}>
        {[['profile', 'My Info'], ['screening', 'Health Screening']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '9px 18px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none',
              borderBottom: `2px solid ${tab === key ? 'var(--red)' : 'transparent'}`,
              color: tab === key ? 'var(--red)' : 'var(--gray-500)',
              marginBottom: '-2px', transition: 'color 0.15s, border-color 0.15s',
              borderRadius: '6px 6px 0 0',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && profile && (
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: '10px', marginBottom: '18px' }}>
            {infoRows.map(([label, value]) => (
              <div key={label} style={{ padding: '13px 14px', background: 'var(--gray-50)', borderRadius: '9px', border: '1px solid var(--gray-100)' }}>
                <div style={{ fontSize: '11px', color: 'var(--gray-400)', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-900)' }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{
            padding: '14px 16px', borderRadius: '10px',
            background: profile.is_eligible ? 'var(--green-light)' : 'var(--red-light)',
            border: `1px solid ${profile.is_eligible ? '#A7DFB9' : 'var(--red-mid)'}`,
            display: 'flex', alignItems: 'flex-start', gap: '12px',
          }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>{profile.is_eligible ? '✅' : '❌'}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: profile.is_eligible ? 'var(--green-dark)' : 'var(--red-dark)' }}>
                {profile.is_eligible ? 'You are eligible to donate' : 'Currently not eligible'}
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--gray-600)', marginTop: '3px', lineHeight: 1.5 }}>
                {profile.eligibility_reason}
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'profile' && profile && (
        <Card style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '2px' }}>What you can donate</div>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '12px', lineHeight: 1.5 }}>
            Whole blood is always included. Opt into platelets/plasma if you're able to make a scheduled apheresis appointment.
          </p>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <ComponentBadge component="whole_blood" />
            {componentsDraft.filter(c => c !== 'whole_blood').length === 0 && (
              <span style={{ fontSize: '11px', color: 'var(--gray-400)' }}>Whole blood only</span>
            )}
          </div>
          {OPTIONAL_COMPONENTS.map(c => {
            const checked = componentsDraft.includes(c.value);
            return (
              <label
                key={c.value}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '10px 12px', borderRadius: '9px', cursor: 'pointer',
                  border: `1.5px solid ${checked ? 'var(--red)' : 'var(--gray-200)'}`,
                  background: checked ? 'var(--red-light)' : 'var(--white)',
                  marginBottom: '8px', transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setComponentsDraft(list => (
                    checked ? list.filter(v => v !== c.value) : [...list, c.value]
                  ))}
                  style={{ marginTop: '2px' }}
                />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--gray-900)' }}>{c.label}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--gray-500)' }}>{c.desc}</div>
                </div>
              </label>
            );
          })}
          {componentsChanged && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
              <Button variant="primary" size="sm" loading={savingComponents} onClick={saveComponents}>Save</Button>
            </div>
          )}
        </Card>
      )}

      {tab === 'profile' && profile && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexDirection: isMobile ? 'column' : 'row' }}>
          <Link to="/donor/certificates" style={{ flex: 1, textDecoration: 'none' }}>
            <Card interactive style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Award size={20} color="var(--red)" />
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)' }}>My Certificates & Badge</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>View donation certificates and your badge tier</div>
              </div>
            </Card>
          </Link>
          <Link to="/account/settings" style={{ flex: 1, textDecoration: 'none' }}>
            <Card interactive style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Settings size={20} color="var(--red)" />
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gray-900)' }}>Account Settings</div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Email, phone, password, city & location</div>
              </div>
            </Card>
          </Link>
        </div>
      )}

      {tab === 'screening' && (
        <Card>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-600)', marginBottom: '18px', lineHeight: 1.65 }}>
            Answer honestly — this determines your donation eligibility and keeps recipients safe.
          </p>

          {eligibility && (
            <Alert variant={eligibility.is_eligible ? 'success' : 'error'} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: eligibility.cooldown_until ? '4px' : 0 }}>{eligibility.reason}</div>
              {eligibility.cooldown_until && (
                <div style={{ fontSize: '12.5px', opacity: 0.85 }}>
                  Eligible again: {new Date(eligibility.cooldown_until).toLocaleDateString()}
                </div>
              )}
            </Alert>
          )}

          {questions.map((q, i) => (
            <div
              key={q.key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                padding: '13px 0',
                borderBottom: i < questions.length - 1 ? '1px solid var(--gray-100)' : 'none',
                gap: '12px',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <span style={{ fontSize: '13.5px', color: 'var(--gray-700)', lineHeight: 1.5, flex: 1 }}>{q.text}</span>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {['Yes', 'No'].map(opt => {
                  const isYes = opt === 'Yes';
                  const active = isYes ? answers[q.key] === true : answers[q.key] === false;
                  return (
                    <button
                      key={opt}
                      onClick={() => setAnswers(a => ({ ...a, [q.key]: isYes }))}
                      style={{
                        padding: '6px 16px', borderRadius: '7px', fontSize: '12.5px',
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)',
                        border: `1.5px solid ${active ? (isYes ? 'var(--red)' : 'var(--green)') : 'var(--gray-200)'}`,
                        background: active ? (isYes ? 'var(--red-light)' : 'var(--green-light)') : 'var(--white)',
                        color: active ? (isYes ? 'var(--red-dark)' : 'var(--green-dark)') : 'var(--gray-500)',
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              loading={submitting}
              onClick={submitScreening}
              style={isMobile ? { width: '100%' } : {}}
            >
              Submit Screening
            </Button>
          </div>
        </Card>
      )}
      </div>
    </PageLayout>
  );
}
